import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Truck, Shield, Phone, Quote, User, Star } from "lucide-react";
import { API, LOGO_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SEO } from "../components/SEO";
import { ProductCard } from "../components/ProductCard";
import { Toast } from "../components/Toast";
import { StarRating, TrustpilotBanner } from "../components/StarRating";
import { FrequentlyReordered } from "../components/FrequentlyReordered";
import HeroSlideshow from "../components/HeroSlideshow";

// Cross-faded hero images (object-cover, hotlink-friendly Unsplash CDN).
// First slide is the original branded healthcare image; remaining slides
// cycle through complementary medical / pharmacy / dental imagery.
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_7533942d-8397-4d43-9cfb-52380ace5505/artifacts/k3sqav7f_Header_Healthcare_v1.webp",
  "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&w=1600&q=70",
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [prodRes, testRes, catRes] = await Promise.all([
          axios.get(`${API}/products?limit=8`),
          axios.get(`${API}/testimonials`).catch(() => ({ data: [] })),
          axios.get(`${API}/categories`).catch(() => ({ data: [] })),
        ]);
        setProducts(prodRes.data);
        setTestimonials(testRes.data || []);
        setDynamicCategories(catRes.data || []);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = async (productId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    const success = await addToCart(productId);
    if (success) {
      setToast({ message: "Added to cart!", type: "success" });
    }
  };

  return (
    <div>
      <SEO />
      {/* Hero Section */}
      <section className="relative text-white py-16 md:py-24 overflow-hidden">
        <HeroSlideshow images={HERO_IMAGES} interval={6000} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003544]/90 via-[#003544]/70 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-2xl">
            <img src={LOGO_URL} alt="Carepoint" className="h-28 md:h-36 lg:h-40 w-auto mb-6" loading="eager" decoding="async" style={{filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.55))'}} />
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="hero-title">
              Quality Healthcare, Dental &amp; Office Supplies at Unbeatable Prices
            </h1>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              The UK's trusted supplier of medical supplies, dental products, care equipment, and printer cartridges. Free next-day delivery on orders over &pound;30.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products" className="bg-white text-[#00a99d] px-8 py-3 rounded font-semibold hover:bg-gray-100 transition" data-testid="shop-now-btn">
                Shop Now
              </Link>
              <Link to="/products?category=Medical%20Supplies" className="border-2 border-white text-white px-8 py-3 rounded font-semibold hover:bg-white/10 transition">
                Browse Medical Supplies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#00a99d]/10 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6 text-[#00a99d]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Free Delivery</p>
                <p className="text-xs text-gray-500">On orders over &pound;30</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#00a99d]/10 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#00a99d]" />
              </div>
              <div>
                <p className="font-semibold text-sm">3 Year Warranty</p>
                <p className="text-xs text-gray-500">On all products</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#00a99d]/10 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-[#00a99d]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Rated 4.9/5</p>
                <p className="text-xs text-gray-500">By our customers</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#00a99d]/10 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-[#00a99d]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Expert Support</p>
                <p className="text-xs text-gray-500">020 3633 8047</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* £500 Instant Credit Banner */}
      <section className="py-8 bg-gradient-to-r from-[#003544] to-[#005EB8]" data-testid="instant-credit-banner">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden grid md:grid-cols-[auto_1fr_auto] gap-6 items-center p-6 md:p-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00a99d] to-[#003544] flex items-center justify-center text-white shadow-lg">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1">
                  Trade Customers
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  &pound;500 Instant Credit Account
                </h3>
              </div>
            </div>
            <div className="text-gray-700 text-sm md:text-base leading-relaxed">
              Order today on a 30-day purchase order — no application form, no waiting.
              Available to established UK organisations: NHS trusts, GP surgeries, care homes,
              dentists, schools and pharmacies. Subject to verification.
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3">
              <Link
                to="/checkout"
                className="bg-[#00a99d] hover:bg-[#008c82] text-white font-semibold px-6 py-3 rounded-lg text-center transition shadow"
                data-testid="instant-credit-cta"
              >
                Choose at checkout &rarr;
              </Link>
              <Link
                to="/about"
                className="border-2 border-[#003544] text-[#003544] font-semibold px-6 py-3 rounded-lg text-center hover:bg-[#003544] hover:text-white transition"
                data-testid="instant-credit-learn-more"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brother Price Crash Promotion Banner */}
      <section className="py-8">
        <div className="max-w-[900px] mx-auto px-4">
          <div
            style={{
              background: "linear-gradient(135deg, #1a2a3a 0%, #0f1a24 100%)",
              color: "white",
              fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
              padding: "30px 20px",
              textAlign: "center",
              borderRadius: "16px",
              boxShadow: "0 20px 40px -12px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,217,102,0.18)",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "optimizeLegibility",
            }}
            data-testid="promo-banner"
          >
            <h2 style={{ color: "#FFD966", margin: "0 0 8px", fontSize: "1.75rem", fontWeight: "900", letterSpacing: "0.5px", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
              BROTHER PRICE CRASH PROMOTION
            </h2>
            <p style={{ fontSize: "16px", marginBottom: "20px", opacity: 0.95, fontWeight: 500 }}>
              <strong>www.carepointuk.net</strong> — Exclusive for Account Customers
            </p>

            <div
              style={{
                background: "#FFD966",
                color: "#1a2a3a",
                padding: "10px 22px",
                borderRadius: "40px",
                display: "inline-block",
                marginBottom: "20px",
                fontWeight: "900",
                letterSpacing: "0.3px",
                fontSize: "14px",
                boxShadow: "0 4px 12px rgba(255,217,102,0.35)",
              }}
            >
              HUGE DISCOUNTS + 12-MONTH PRICE FREEZE
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "20px", margin: "20px 0" }}>
              <div style={{ background: "linear-gradient(180deg, #2c3e4e 0%, #1f2d3a 100%)", padding: "20px", borderRadius: "14px", width: "260px", boxShadow: "0 10px 24px rgba(0,0,0,0.35)" }}>
                <div style={{ background: "white", borderRadius: "10px", padding: "12px", marginBottom: "14px", height: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src="https://www.cartridgesave.co.uk/media/catalog/product/t/n/tn3480-7850659.jpg"
                    alt="Brother TN3480 Toner"
                    loading="eager"
                    decoding="async"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", imageRendering: "auto" }}
                  />
                </div>
                <h3 style={{ margin: "0 0 6px", fontSize: "1.05rem", fontWeight: 700, letterSpacing: "0.3px" }}>TN-3480 Toner</h3>
                <p style={{ fontSize: "32px", fontWeight: "900", color: "#FFD966", margin: "6px 0", letterSpacing: "0.5px", textShadow: "0 2px 6px rgba(0,0,0,0.35)" }}>&pound;79.99</p>
                <p style={{ margin: "4px 0", fontSize: "13px", fontWeight: 600, color: "#7CFC9D" }}>Save 30% OFF</p>
              </div>
              <div style={{ background: "linear-gradient(180deg, #2c3e4e 0%, #1f2d3a 100%)", padding: "20px", borderRadius: "14px", width: "260px", boxShadow: "0 10px 24px rgba(0,0,0,0.35)" }}>
                <div style={{ background: "white", borderRadius: "10px", padding: "12px", marginBottom: "14px", height: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src="https://www.cartridgesave.co.uk/media/catalog/product/h/t/https1cs1.comstaticimagesitemsoriginaldr3400.png"
                    alt="Brother DR3400 Drum"
                    loading="eager"
                    decoding="async"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", imageRendering: "auto" }}
                  />
                </div>
                <h3 style={{ margin: "0 0 6px", fontSize: "1.05rem", fontWeight: 700, letterSpacing: "0.3px" }}>DR-3400 Drum</h3>
                <p style={{ fontSize: "32px", fontWeight: "900", color: "#FFD966", margin: "6px 0", letterSpacing: "0.5px", textShadow: "0 2px 6px rgba(0,0,0,0.35)" }}>&pound;89.99</p>
                <p style={{ margin: "4px 0", fontSize: "13px", fontWeight: 600, color: "#7CFC9D" }}>Save 25% OFF</p>
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #6e2bb0 0%, #00a99d 100%)",
                color: "white",
                padding: "20px",
                borderRadius: "16px",
                margin: "24px auto",
                maxWidth: "640px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
              }}
              data-testid="voucher-promo"
            >
              <div style={{ fontSize: "12px", letterSpacing: "2px", fontWeight: "bold", color: "#FFD966", textTransform: "uppercase", marginBottom: "6px" }}>
                Order 2 Toners + 2 Drums
              </div>
              <div style={{ fontSize: "26px", fontWeight: "900", marginBottom: "4px", lineHeight: 1.1 }}>
                Qualify for a FREE
              </div>
              <div style={{ fontSize: "44px", fontWeight: "900", color: "#FFD966", lineHeight: 1, margin: "4px 0 14px" }}>
                &pound;50 Pre-Paid Gift Card
              </div>

              {/* Voucher cards — Love2Shop + Amazon (£50) */}
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "12px 0 18px", height: "150px", position: "relative" }}>
                {/* Love2Shop Purple Card (rotated left) */}
                <div
                  style={{
                    width: "210px",
                    height: "132px",
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #6e2bb0 0%, #3a1370 100%)",
                    padding: "14px",
                    color: "white",
                    position: "relative",
                    transform: "rotate(-6deg) translateX(15px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 70% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)" }}></div>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontSize: "14px", fontWeight: "900", letterSpacing: "0.5px" }}>LOVE2SHOP</div>
                    <div style={{ fontSize: "11px", opacity: 0.95, marginTop: "2px" }}>Purple Card</div>
                  </div>
                  <div style={{ position: "absolute", left: "14px", bottom: "14px", color: "white", fontSize: "10px", letterSpacing: "0.3px", opacity: 0.95 }}>
                    <div>VALID FROM</div>
                    <div style={{ fontWeight: "bold" }}>09/26</div>
                  </div>
                  {/* faux heart */}
                  <div style={{ position: "absolute", right: "14px", top: "30%", width: "44px", height: "40px", background: "#4a1a78", borderRadius: "8px 8px 24px 8px", transform: "rotate(-45deg)", opacity: 0.8 }}></div>
                </div>

                {/* Amazon £50 Card (rotated right, overlapping) */}
                <div
                  style={{
                    width: "230px",
                    height: "138px",
                    borderRadius: "14px",
                    background: "#232F3E",
                    padding: "16px 18px",
                    color: "white",
                    position: "relative",
                    transform: "rotate(5deg) translateX(-30px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
                    fontFamily: "'Amazon Ember', Helvetica, Arial, sans-serif",
                  }}
                  data-testid="voucher-amazon-card"
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: "30px", fontWeight: "700", lineHeight: 1 }}>&pound;50</div>
                    <div style={{ fontSize: "13px", fontWeight: "500", color: "#fff" }}>
                      amazon<span style={{ color: "#FF9900" }}>.co.uk</span>
                    </div>
                  </div>
                  {/* Amazon smile arrow */}
                  <svg viewBox="0 0 200 50" style={{ position: "absolute", left: "20px", right: "20px", bottom: "16px", width: "calc(100% - 40px)", height: "32px" }} xmlns="http://www.w3.org/2000/svg">
                    <path d="M 10 10 Q 100 50, 190 12" stroke="#FF9900" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <polygon points="180,10 195,8 190,22" fill="#FF9900" />
                  </svg>
                </div>
              </div>

              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.18)", color: "white", padding: "8px 16px", borderRadius: "30px", fontSize: "13px", fontWeight: "bold" }}>
                Invoiced on account · 30-day credit · Free delivery
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.95)",
                  marginTop: "16px",
                  lineHeight: 1.55,
                  maxWidth: "560px",
                  margin: "16px auto 0",
                  fontWeight: 500,
                  letterSpacing: "0.2px",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                <strong style={{ color: "#FFD966" }}>T&amp;Cs apply:</strong> Pre-paid gift card supplied by our manufacturer
                partner. Released and dispatched via Royal Mail Special Delivery within 24 hours
                of payment of invoice. UK mainland only. One gift card per qualifying order.
                Subject to stock and credit approval.
              </p>
            </div>

            <div
              style={{
                background: "#e67e22",
                padding: "12px",
                borderRadius: "12px",
                margin: "15px auto",
                maxWidth: "400px",
                fontWeight: "bold",
              }}
            >
              Order now – Your prices frozen for 12 months!
            </div>

            <div style={{ fontSize: "14px", marginTop: "20px" }}>
              <Link
                to="/products?search=brother"
                style={{
                  background: "#FFD966",
                  color: "#1a2a3a",
                  padding: "12px 24px",
                  textDecoration: "none",
                  fontWeight: "bold",
                  borderRadius: "40px",
                  display: "inline-block",
                }}
              >
                LOCK IN YOUR PRICES TODAY
              </Link>
            </div>

            <div style={{ marginTop: "20px", padding: "10px", background: "#2c3e4e", borderRadius: "12px" }}>
              <p style={{ margin: "0" }}>
                <strong>SIMPLY EMAIL US</strong> or <strong>GIVE US A CALL</strong> – we'll invoice you!
              </p>
              <p style={{ margin: "5px 0 0", fontSize: "14px" }}>Quote code: FREEZE34</p>
            </div>

            <p style={{ fontSize: "11px", marginTop: "20px", opacity: 0.7 }}>
              Terms apply – UK mainland free delivery | 30-day credit subject to approved account
            </p>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <FrequentlyReordered variant="strip" limit={6} />
          </div>

          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Featured Products</h2>
            <Link to="/products" className="text-[#00a99d] font-medium hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="products-grid" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trustpilot + Customer Reviews */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Trusted by Over 100,000 Customers</h2>
            <p className="text-gray-500">See what our customers say about us</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {/* Trustpilot Badge */}
            <div className="md:col-span-1">
              <TrustpilotBanner />
            </div>

            {/* Customer Testimonials */}
            <div className="md:col-span-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials">
              {testimonials.slice(0, 6).map((t) => (
                <div key={t.id} className="bg-white border rounded-xl p-5 flex flex-col" data-testid={`testimonial-${t.id}`}>
                  <StarRating rating={t.rating} size="w-4 h-4" />
                  <p className="text-gray-700 text-sm mt-3 flex-1 leading-relaxed">
                    <Quote className="w-4 h-4 text-[#00a99d] inline-block mr-1 -mt-1" />
                    {t.text.length > 160 ? t.text.slice(0, 160) + '...' : t.text}
                  </p>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                    <div className="w-8 h-8 rounded-full bg-[#00a99d]/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#00a99d]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category - dynamic from API */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Shop by Category</h2>
            <Link to="/products" className="text-[#00a99d] font-medium hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="shop-by-category">
            {dynamicCategories.slice(0, 10).map(cat => (
              <Link
                key={cat.name}
                to={`/products?category=${encodeURIComponent(cat.name)}`}
                className="card p-4 hover:shadow-lg transition group text-center"
                data-testid={`category-tile-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h3 className="font-semibold group-hover:text-[#00a99d]">{cat.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{cat.count.toLocaleString()} products</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
